import { api } from './api'

export const subscriptionService = {
  async getCurrent() {
    return api.get('/subscription/current', true)
  },

  async getUsage() {
    return api.get('/subscription/usage', true)
  },

  async getEffectiveAccess() {
    return api.get('/subscription/effective', true)
  },

  async getPlans() {
    return api.get('/subscription/plans', true)
  },

  async getBillingConfig() {
    return api.get('/subscription/billing-config', true)
  },

  async postCheckout(planType, options = {}) {
    return api.post('/subscription/checkout', {
      plan_type: planType,
      ...(options.fiscalProfile ? { fiscal_profile: options.fiscalProfile } : {})
    }, true)
  },

  async createCheckout(planType, options = {}) {
    return this.postCheckout(planType, options)
  },

  async createBillingPortal() {
    return api.post('/subscription/portal', {}, true)
  },

  async cancelSubscription() {
    return api.post('/subscription/cancel', {}, true)
  },

  async resumeSubscription() {
    return api.post('/subscription/resume', {}, true)
  },

  async ackDowngrade() {
    return api.post('/subscription/ack-downgrade', {}, true)
  },

  async getPayments() {
    return api.get('/subscription/payments', true)
  },

  async getInvoices() {
    return api.get('/subscription/invoices', true)
  },

  async getReceipt(sessionId) {
    return api.get(`/subscription/receipt/${encodeURIComponent(sessionId)}`, true)
  },

  async getCurrentPromoCode() {
    return api.get('/promo-codes/company/current', true)
  },

  async validatePromoCode(code) {
    return api.post('/promo-codes/validate', { code }, true)
  },

  async redeemPromoCode(code) {
    return api.post('/promo-codes/redeem', { code }, true)
  },

  async cancelPromoCode() {
    return api.post('/promo-codes/company/cancel', {}, true)
  },

  async getPromoCodeHistory() {
    return api.get('/promo-codes/company/redemptions', true)
  },

  async fetchBillingHistory() {
    try {
      const data = await this.getInvoices()
      const rows = parseSubscriptionBillingHistoryList(data)
      if (rows.length > 0) return rows
    } catch { }

    try {
      const data = await this.getPayments()
      const rows = parseSubscriptionBillingHistoryList(data)
      if (rows.length > 0) return rows
    } catch { }

    return []
  }
}

function unwrapData(data) {
  if (!data || typeof data !== 'object') return null
  if (data.success === false) return null
  return data.data ?? data
}

export function parseCurrentPromoCodePayload(data) {
  const current = unwrapData(data)
  if (!current || typeof current !== 'object' || Array.isArray(current)) return null
  const promo = current.promo_code ?? current.promoCode ?? {}
  const metadata = current.metadata && typeof current.metadata === 'object'
    ? current.metadata
    : current.redemption?.metadata && typeof current.redemption.metadata === 'object'
      ? current.redemption.metadata
      : {}
  const status = String(current.status ?? current.redemption?.status ?? '').toLowerCase()
  const canceledAt = metadata.canceled_at ??
    current.canceled_at ??
    current.canceledAt ??
    current.redemption?.canceled_at ??
    current.redemption?.canceledAt ??
    null
  const endsAt = current.ends_at ?? current.endsAt ?? current.redemption?.ends_at ?? null
  const code = promo.code ?? current.code ?? ''
  const hasPromoIdentity = Boolean(current.id || code || current.promo_code_id || current.promoCodeId)
  const endsAtTime = endsAt ? new Date(endsAt).getTime() : null
  const isExpired = Number.isFinite(endsAtTime) ? endsAtTime < Date.now() : false
  const isCanceled = Boolean(canceledAt) || status === 'canceled' || status === 'cancelled'
  const explicitlyInactive = current.is_active === false || current.isActive === false || current.redemption?.is_active === false
  if (!hasPromoIdentity) return null
  return {
    id: current.id,
    code,
    campaignName: promo.campaign_name ?? current.campaign_name ?? '',
    description: promo.description ?? current.description ?? '',
    planType: promo.plan_type ?? current.plan_type ?? '',
    durationDays: promo.duration_days ?? current.duration_days ?? null,
    startsAt: current.starts_at ?? current.startsAt ?? current.redemption?.starts_at ?? null,
    endsAt,
    redeemedAt: current.redemption?.redeemed_at ?? current.redeemed_at ?? null,
    redeemedBy: current.redemption?.redeemedByUser?.username ??
      current.redemption?.redeemedByUser?.email ??
      current.redeemed_by_name ??
      null,
    canceledAt,
    isCanceled,
    isActive: hasPromoIdentity && !isCanceled && !explicitlyInactive && !isExpired
  }
}

export function parsePromoCodeValidationPayload(data) {
  if (!data || typeof data !== 'object') return null
  const d = data.data && typeof data.data === 'object' ? data.data : {}
  return {
    success: data.success === true,
    valid: data.valid === true,
    status: data.status ?? null,
    message: data.message ?? '',
    code: d.code ?? '',
    planType: d.plan_type ?? '',
    durationDays: d.duration_days ?? null,
    projectedEndsAt: d.projected_ends_at ?? null,
    warningPossibleConflict: d.warning_possible_conflict === true
  }
}

export function normalizePromoCodeRedemption(item) {
  if (!item || typeof item !== 'object') return null
  const promo = item.promo_code ?? item.promoCode ?? {}
  const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : {}
  return {
    id: item.id,
    code: promo.code ?? item.code ?? '',
    campaignName: promo.campaign_name ?? item.campaign_name ?? '',
    description: promo.description ?? item.description ?? '',
    planType: item.plan_type ?? promo.plan_type ?? '',
    durationDays: promo.duration_days ?? item.duration_days ?? null,
    startsAt: item.starts_at ?? null,
    endsAt: item.ends_at ?? null,
    redeemedAt: item.redeemed_at ?? item.created_at ?? null,
    canceledAt: metadata.canceled_at ?? null,
    redeemedBy: item.redeemedByUser?.username ??
      item.redeemedByUser?.email ??
      item.user?.username ??
      item.user?.email ??
      null,
    isCanceled: Boolean(metadata.canceled_at),
    isActive: !metadata.canceled_at && item.ends_at && new Date(item.ends_at).getTime() >= Date.now()
  }
}

export function parsePromoCodeHistoryList(data) {
  const d = unwrapData(data)
  const rows = Array.isArray(d) ? d : Array.isArray(d?.rows) ? d.rows : []
  return rows.map(normalizePromoCodeRedemption).filter(Boolean)
}

export function planTypeFromCheckoutResponse(data) {
  if (!data || typeof data !== 'object') return null
  const nested = data.data != null && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null
  const pt = nested?.plan_type ?? data.plan_type
  if (pt == null || pt === '') return null
  return String(pt).trim()
}

export function checkoutStripeUrlFromResponse(data) {
  if (!data || typeof data !== 'object' || data.success !== true) return null
  const d = data.data
  if (d && typeof d === 'object' && typeof d.url === 'string' && d.url.startsWith('http')) return d.url
  return null
}

export function billingPortalUrlFromResponse(data) {
  return checkoutStripeUrlFromResponse(data)
}

export function parseSubscriptionPlansList(data) {
  if (!data || typeof data !== 'object' || data.success === false) return []
  const arr = data.data
  if (!Array.isArray(arr)) return []
  return arr.filter((p) => p && typeof p === 'object')
}

export function parseSubscriptionUsagePayload(data) {
  if (!data || typeof data !== 'object' || data.success === false) return null
  const d = data.data
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null
  return d
}

function isStripeInternalId(id) {
  if (!id) return false
  const s = String(id)
  return s.startsWith('in_') || s.startsWith('pi_') || s.startsWith('ch_') || s.startsWith('sub_')
}

function isStripeInvoiceObject(item) {
  if (!item || typeof item !== 'object') return false
  if (item.object === 'invoice') return true
  return !!(item.hosted_invoice_url || item.invoice_pdf || item.billing_reason)
}

function coerceBillingDate(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
  if (typeof value === 'string' && value.trim() !== '') return value
  return null
}

function resolveBillingAmount(item) {
  if (item.amount_cents != null && item.amount_cents !== '') {
    const n = Number(item.amount_cents)
    if (Number.isFinite(n)) return n / 100
  }
  for (const key of ['amount_due', 'amount_paid', 'amount_total', 'total', 'subtotal']) {
    if (item[key] != null && item[key] !== '') {
      const n = Number(item[key])
      if (Number.isFinite(n) && isStripeInvoiceObject(item)) return n / 100
    }
  }
  for (const key of ['amount', 'total', 'price']) {
    if (item[key] != null && item[key] !== '') {
      const n = Number(item[key])
      if (Number.isFinite(n)) return n
    }
  }
  return null
}

export function normalizeSubscriptionBillingRecord(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null

  const hasInvoiceFields = !!(
    item.object === 'invoice' || item.hosted_invoice_url || item.invoice_pdf ||
    item.billing_reason || item.invoice_number || item.number ||
    item.description || item.amount_paid != null || item.amount_total != null
  )
  if (!hasInvoiceFields) return null

  const date = coerceBillingDate(
    item.effective_at ?? item.status_transitions?.paid_at ?? item.created ??
    item.date ?? item.created_at ?? item.paid_at ?? item.invoice_date ?? item.billing_date ?? null
  )
  const amount = resolveBillingAmount(item)
  const invoiceNumber = item.number != null && item.number !== '' ? String(item.number) :
    item.invoice_number != null && item.invoice_number !== '' ? String(item.invoice_number) : null
  const stripeId = item.stripe_invoice_id != null ? String(item.stripe_invoice_id) :
    item.id != null && isStripeInternalId(String(item.id)) ? String(item.id) : null
  const rawId = item.id != null && !isStripeInternalId(String(item.id)) ? String(item.id) : null
  const id = invoiceNumber ?? rawId ?? stripeId ?? null
  const formattedRawId = rawId != null
    ? /^\d+$/.test(rawId) ? `FAC-${rawId.padStart(5, '0')}` : rawId
    : null
  const displayId = invoiceNumber ?? formattedRawId ?? stripeId ?? `INV-${String(date ?? 'sin-fecha')}`

  if (date == null && id == null && amount == null) return null

  const line0 = Array.isArray(item.lines?.data) && item.lines.data[0] ? item.lines.data[0] :
    Array.isArray(item.line_items) && item.line_items[0] ? item.line_items[0] : null
  const lineDesc = line0 && typeof line0 === 'object'
    ? line0.description ?? line0.plan?.nickname ?? line0.plan?.name : null
  const pdfUrl = item.invoice_pdf ?? item.hosted_invoice_url ?? item.pdf_url ??
    item.receipt_url ?? item.invoice_url ?? null

  return {
    id: id ?? `INV-${String(date ?? 'sin-fecha')}`,
    displayId,
    date,
    description: item.description != null && item.description !== '' ? String(item.description) :
      lineDesc != null && lineDesc !== '' ? String(lineDesc) :
      item.plan_label != null ? String(item.plan_label) :
      item.plan_name != null ? String(item.plan_name) : 'Suscripción',
    amount,
    currency: item.currency != null && item.currency !== '' ? String(item.currency).toUpperCase() : 'MXN',
    status: item.status != null ? String(item.status).toLowerCase() : 'paid',
    pdfUrl: pdfUrl != null && pdfUrl !== '' ? String(pdfUrl) : null
  }
}

const BILLING_ARRAY_KEYS = [
  'invoices', 'billing_history', 'payment_history', 'payments',
  'billing_records', 'invoice_history', 'stripe_invoices',
  'receipts', 'transactions', 'items', 'records'
]

function extractBillingHistoryArray(d) {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return []
  for (const k of BILLING_ARRAY_KEYS) {
    const v = d[k]
    if (Array.isArray(v) && v.length > 0) return v
  }
  const billing = d.billing
  if (billing && typeof billing === 'object' && !Array.isArray(billing)) {
    for (const k of ['invoices', 'payments', 'history', 'records']) {
      const v = billing[k]
      if (Array.isArray(v) && v.length > 0) return v
    }
  }
  return []
}

export function parseSubscriptionBillingHistoryList(data) {
  if (!data || typeof data !== 'object') return []
  if (data.success === false) return []
  const d = data.data
  if (Array.isArray(d)) return d.map(normalizeSubscriptionBillingRecord).filter(Boolean)
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const nested = extractBillingHistoryArray(d)
    if (nested.length > 0) return nested.map(normalizeSubscriptionBillingRecord).filter(Boolean)
  }
  const top = extractBillingHistoryArray(data)
  if (top.length > 0) return top.map(normalizeSubscriptionBillingRecord).filter(Boolean)
  return []
}

export function mergeBillingHistoryRecords(...lists) {
  const byId = new Map()
  const byDateAmount = new Map()

  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const row of list) {
      if (!row || typeof row !== 'object') continue
      const idKey = row.id != null ? String(row.id) : null
      const dateAmountKey = row.date && row.amount != null
        ? `${String(row.date).slice(0, 10)}-${Number(row.amount).toFixed(2)}` : null

      if (idKey && byId.has(idKey)) continue

      if (dateAmountKey && byDateAmount.has(dateAmountKey)) {
        const existing = byDateAmount.get(dateAmountKey)
        const existingIsStripe = isStripeInternalId(existing.id)
        const newIsStripe = isStripeInternalId(row.id)
        if (existingIsStripe && !newIsStripe) {
          const merged = { ...existing, ...row, pdfUrl: row.pdfUrl ?? existing.pdfUrl, displayId: row.displayId ?? row.id }
          byDateAmount.set(dateAmountKey, merged)
          if (idKey) byId.set(idKey, merged)
          if (existing.id) byId.delete(String(existing.id))
        } else if (!existingIsStripe && newIsStripe) {
          if (row.pdfUrl && !existing.pdfUrl) {
            const merged = { ...existing, pdfUrl: row.pdfUrl }
            byDateAmount.set(dateAmountKey, merged)
            if (existing.id) byId.set(String(existing.id), merged)
          }
          if (idKey) byId.set(idKey, existing)
        }
        continue
      }

      const entry = { ...row, displayId: row.displayId ?? row.id }
      if (idKey) byId.set(idKey, entry)
      if (dateAmountKey) byDateAmount.set(dateAmountKey, entry)
    }
  }

  const seen = new Set()
  const result = []
  for (const entry of byDateAmount.values()) {
    const k = entry.id != null ? String(entry.id) : JSON.stringify(entry)
    if (!seen.has(k)) { seen.add(k); result.push(entry) }
  }
  for (const entry of byId.values()) {
    const k = entry.id != null ? String(entry.id) : JSON.stringify(entry)
    if (!seen.has(k)) { seen.add(k); result.push(entry) }
  }
  return result
}

export function parseSubscriptionCurrentPayload(data) {
  if (!data || typeof data !== 'object') return null
  if (data.success === false) return null
  const d = data.data
  if (!d || typeof d !== 'object') return null

  const sub = d.subscription ?? null
  const cardLast4 = sub?.card_last4 ?? sub?.last4 ?? d.card_last4 ?? d.last4 ?? null
  const cardBrand = sub?.card_brand ?? sub?.brand ?? d.card_brand ?? d.brand ?? null
  const nextBillingDate = sub?.current_period_end ?? sub?.next_billing_date ??
    d.current_period_end ?? d.access_until ?? null
  const paymentMethodLabel = sub?.payment_method_label ?? d.payment_method_label ??
    (cardBrand && cardLast4 ? `${String(cardBrand)} •••• ${cardLast4}` : null)

  return {
    subscription: sub,
    plan: d.plan ?? null,
    effectivePlan: d.effective_plan ?? null,
    usage: d.usage ?? null,
    accessUntil: d.access_until ?? null,
    cancelAtPeriodEnd: d.cancel_at_period_end ?? false,
    downgradeAckPending: (d.downgrade_ack_pending ?? sub?.downgrade_ack_pending) === true,
    isAccessActive: d.is_access_active ?? null,
    effectiveAccess: d.effective_access ?? null,
    paymentInfo: {
      cardLast4: cardLast4 != null ? String(cardLast4) : null,
      paymentMethodLabel: paymentMethodLabel != null ? String(paymentMethodLabel) : null,
      cardBrand: cardBrand != null ? String(cardBrand) : null,
      nextBillingDate: nextBillingDate != null ? String(nextBillingDate) : null
    }
  }
}

export function parseSubscriptionEffectivePayload(data) {
  if (!data || typeof data !== 'object' || data.success === false) return null
  const d = data.data
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null
  return {
    role: d.role ?? null,
    effectivePlanType: normalizeSubscriptionPlanType(d.effective_plan_type),
    plan: d.plan ?? null,
    isPromoActive: d.is_promo_active === true,
    isAccessActive: d.is_access_active ?? null,
    effectiveAccess: d.effective_access ?? null
  }
}

export function normalizeSubscriptionPlanType(raw) {
  if (raw == null || raw === '') return 'free'
  const s = String(raw).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  if (s === 'free' || s === 'starter' || s === 'gratuito') return 'free'
  if (s === 'basic' || s === 'basico') return 'basic'
  if (s === 'pro' || s === 'professional' || s === 'profesional') return 'pro'
  if (s === 'annual' || s === 'yearly' || s === 'anual') return 'annual'
  if (s === 'enterprise' || s === 'empresarial') return 'enterprise'
  // Planes internos de prueba: se mapean a su tier equivalente (igual que
  // PLAN_HIERARCHY en el backend) para que el gating los trate correctamente.
  if (s === 'plantesta' || s === 'plantestc') return 'pro'
  if (s === 'plantestb' || s === 'plantestd') return 'basic'
  return 'free'
}

export function subscriptionStateFromCheckout(data) {
  const raw = data?.data != null && typeof data.data === 'object' && !Array.isArray(data.data)
    ? data.data
    : data && typeof data === 'object' ? data : null
  if (!raw || typeof raw !== 'object') {
    return { planType: null, nextBillingDate: null, paymentMethodLabel: null, cardLast4: null }
  }
  const planType = raw.plan_type != null && raw.plan_type !== '' ? String(raw.plan_type).trim() : null
  const nextBillingDate = raw.next_billing_date ?? raw.next_charge_date ?? raw.next_charge ?? raw.current_period_end ?? null
  const cardLast4 = raw.card_last4 ?? raw.last4 ?? raw.card_last_four ?? null
  const paymentMethodLabel = raw.payment_method_label ?? raw.payment_method ??
    (raw.card_brand && cardLast4 ? `${String(raw.card_brand)} •••• ${cardLast4}` : null)
  return {
    planType,
    nextBillingDate: nextBillingDate != null ? String(nextBillingDate) : null,
    paymentMethodLabel: paymentMethodLabel != null ? String(paymentMethodLabel) : null,
    cardLast4: cardLast4 != null ? String(cardLast4) : null
  }
}

export function parseSubscriptionPaymentsList(data) {
  if (!data || typeof data !== 'object' || data.success === false) return []
  const arr = data.data
  return Array.isArray(arr) ? arr.filter((item) => item && typeof item === 'object') : []
}

function normalizeStripeDate(value) {
  if (value == null || value === '') return value
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 10_000_000_000 ? value * 1000 : value).toISOString()
  }
  return value
}

function normalizeStripeAmount(value, shouldConvertFromMinorUnits) {
  if (value == null || value === '') return value
  const amount = Number(value)
  if (!Number.isFinite(amount)) return value
  return shouldConvertFromMinorUnits ? amount / 100 : amount
}

export function parseSubscriptionInvoicesList(data) {
  if (!data || typeof data !== 'object' || data.success === false) return []
  const arr = data.data
  if (!Array.isArray(arr)) return []

  return arr
    .filter((item) => item && typeof item === 'object')
    .map((invoice) => ({
      ...invoice,
      // Solo normalizamos fechas (unix → ISO). Los montos ya vienen en pesos desde el backend.
      created: normalizeStripeDate(invoice.created),
      period_start: normalizeStripeDate(invoice.period_start),
      period_end: normalizeStripeDate(invoice.period_end),
      paid_at: normalizeStripeDate(invoice.paid_at ?? invoice.status_transitions?.paid_at)
    }))
}

export function parseSubscriptionReceiptPayload(data) {
  if (!data || typeof data !== 'object' || data.success === false) return null
  const receipt = data.data?.receipt
  return receipt && typeof receipt === 'object' ? receipt : null
}

export function parseBillingConfigPayload(data) {
  if (!data || typeof data !== 'object' || data.success === false) return null
  const config = data.data
  return config && typeof config === 'object' ? config : null
}
